import { format } from 'date-fns';
import { usePayrollHoursText } from '../../hooks/usePayrollHoursText';
import { payrollMinutesFromHours } from '../../lib/payrollHoursLabel';
import { Calendar, ChevronDown, Clock, Inbox, Loader2, Menu, Plus, Users } from 'lucide-react';
import type { PayrollPeriodSettings } from '../../lib/payrollPeriodSettings';
import {
  formatPayrollPeriodType,
  getPayrollPeriodDueLabel,
} from '../../lib/payrollPeriodSettings';

interface PayrollEntriesPanelProps {
  selectedRangeLabel: string;
  dateRange: { start: Date; end: Date };
  payrollSettings: PayrollPeriodSettings | null;
  totalEarnings: number;
  totalHours: number;
  resourceCount: number;
  isLoading?: boolean;
  onAddEntry?: () => void;
  onAddBonus?: () => void;
  onOpenDateRange?: () => void;
  isDateRangeOpen?: boolean;
  onOpenHistory?: () => void;
  onOpenRequests?: () => void;
  onOpenRunPayroll?: () => void;
  pendingRequestsCount?: number;
  children: React.ReactNode;
}

export default function PayrollEntriesPanel({
  selectedRangeLabel,
  dateRange,
  payrollSettings,
  totalEarnings,
  totalHours,
  resourceCount,
  isLoading = false,
  onAddEntry,
  onAddBonus,
  onOpenDateRange,
  isDateRangeOpen = false,
  onOpenHistory,
  onOpenRequests,
  onOpenRunPayroll,
  pendingRequestsCount = 0,
  children,
}: PayrollEntriesPanelProps) {
  const periodDatesLabel = `${format(dateRange.start, 'MMM d, yyyy')} – ${format(dateRange.end, 'MMM d, yyyy')}`;
  const dueLabel = payrollSettings ? getPayrollPeriodDueLabel(dateRange.end) : null;
  const periodTypeLabel = payrollSettings
    ? formatPayrollPeriodType(payrollSettings.period_type)
    : null;

  const totalMinutes = payrollMinutesFromHours(totalHours);
  const hoursText = usePayrollHoursText([totalMinutes]);
  const hoursLabel = hoursText(totalMinutes);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200/50 overflow-hidden">
      <div className="px-3 sm:px-4 py-2.5 border-b border-gray-100">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              data-date-range-button
              onClick={onOpenDateRange}
              className="inline-flex h-9 min-w-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-[14px] font-medium text-gray-900 active:bg-gray-100 transition-colors touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Calendar className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
              <span className="truncate">{selectedRangeLabel}</span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} aria-hidden />
            </button>

            <p className="hidden lg:flex min-w-0 items-center gap-x-2 text-[13px] text-gray-500">
              <span className="truncate">{periodDatesLabel}</span>
              {periodTypeLabel ? <span className="shrink-0 font-medium text-blue-700">{periodTypeLabel}</span> : null}
              <span className="inline-flex shrink-0 items-center gap-1">
                <Users className="h-3.5 w-3.5 text-gray-400" aria-hidden />
                {isLoading ? '—' : `${resourceCount} ${resourceCount === 1 ? 'resource' : 'resources'}`}
              </span>
              <span className="inline-flex shrink-0 items-center gap-1">
                <Clock className="h-3.5 w-3.5 text-gray-400" aria-hidden />
                {isLoading ? '—' : hoursLabel}
              </span>
              {dueLabel ? <span className="shrink-0 font-medium text-amber-700">{dueLabel}</span> : null}
            </p>

            <div className="ml-auto flex items-center gap-2 lg:hidden">
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" aria-label="Loading total payroll" />
              ) : (
                <p className="text-[18px] font-bold leading-none text-gray-900 tabular-nums">
                  ${totalEarnings.toFixed(2)}
                </p>
              )}
              {onAddBonus && (
                <button
                  type="button"
                  onClick={onAddBonus}
                  className="inline-flex h-9 items-center rounded-lg border border-gray-200 px-2.5 text-[13px] font-semibold text-gray-800 active:bg-gray-50 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  Bonus
                </button>
              )}
              {onAddEntry && (
                <button
                  type="button"
                  onClick={onAddEntry}
                  aria-label="Add Entry"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-800 active:bg-gray-50 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1.5 lg:ml-auto lg:flex lg:items-center">
            <button
              type="button"
              onClick={onOpenHistory}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 text-[13px] font-medium text-gray-800 active:bg-gray-50 transition-colors touch-manipulation lg:px-2.5"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Menu className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
              History
            </button>
            <button
              type="button"
              onClick={onOpenRequests}
              className="relative inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 text-[13px] font-medium text-gray-800 active:bg-gray-50 transition-colors touch-manipulation lg:px-2.5"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Inbox className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
              <span className="hidden sm:inline">Time Requests</span>
              <span className="sm:hidden">Requests</span>
              {pendingRequestsCount > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold text-white">
                  {pendingRequestsCount > 99 ? '99+' : pendingRequestsCount}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={onOpenRunPayroll}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-2 text-[13px] font-semibold text-white active:bg-blue-700 transition-colors touch-manipulation lg:px-2.5"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              <span className="hidden sm:inline">Process Payroll</span>
              <span className="sm:hidden">Process</span>
            </button>
            {isLoading ? (
              <Loader2 className="hidden h-5 w-5 animate-spin text-blue-600 lg:block" aria-label="Loading total payroll" />
            ) : (
              <p className="hidden pl-1 text-[20px] font-bold leading-none text-gray-900 tabular-nums lg:block">
                ${totalEarnings.toFixed(2)}
              </p>
            )}
            {onAddBonus && (
              <button
                type="button"
                onClick={onAddBonus}
                className="hidden h-9 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-[13px] font-semibold text-gray-800 active:bg-gray-50 transition-colors touch-manipulation lg:inline-flex"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Plus className="h-3.5 w-3.5" />
                Add bonus
              </button>
            )}
            {onAddEntry && (
              <button
                type="button"
                onClick={onAddEntry}
                className="hidden h-9 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-[13px] font-semibold text-gray-800 active:bg-gray-50 transition-colors touch-manipulation lg:inline-flex"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Plus className="h-3.5 w-3.5" />
                Add Entry
              </button>
            )}
          </div>
        </div>

        <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-gray-500 lg:hidden">
          <span>{periodDatesLabel}</span>
          {periodTypeLabel ? <span className="font-medium text-blue-700">{periodTypeLabel}</span> : null}
          <span className="inline-flex items-center gap-1">
            <Users className="h-3.5 w-3.5 text-gray-400" aria-hidden />
            {isLoading ? '—' : `${resourceCount} ${resourceCount === 1 ? 'resource' : 'resources'}`}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5 text-gray-400" aria-hidden />
            {isLoading ? '—' : hoursLabel}
          </span>
          {dueLabel ? <span className="font-medium text-amber-700">{dueLabel}</span> : null}
        </p>
      </div>

      <div className="p-2 sm:p-3 lg:p-4">{children}</div>
    </div>
  );
}
